import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, faqs } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;

    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9s97bfnahb7382ovseg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        if (
          typeof msg === 'string' &&
          (msg.includes('spam') || msg.includes('form data is spam'))
        ) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const breadcrumbItems = [{ label: '首页', href: '/' }, { label: '关于我们' }];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 穿搭换装指南',
    description: '穿搭换装指南致力于为爱美女性提供最专业、最实用的换装技巧与穿搭指南。',
    url: 'https://yourdomain.com/about',
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Hero */}
          <section className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20studio%20workspace%20with%20mannequins%20and%20fabric%20swatches%2C%20bright%20airy%20interior%20with%20warm%20natural%20lighting%2C%20cream%20and%20beige%20minimalist%20design%2C%20fashion%20brand%20aesthetic%2C%20feminine%20professional%20atmosphere&width=1800&height=600&seq=about-hero&orientation=landscape"
              alt="关于穿搭换装指南"
              className="absolute inset-0 w-full h-full object-cover object-top"
              title="关于我们 - 穿搭换装指南"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
              <h1 className="font-heading text-xl md:text-3xl text-background-50">关于我们</h1>
              <p className="mt-2 text-sm md:text-base text-background-100/80">
                了解穿搭换装指南的品牌故事
              </p>
            </div>
          </section>

          <Breadcrumb items={breadcrumbItems} />

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="font-heading text-xl md:text-3xl text-foreground-900">
                  品牌故事
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  始于热爱，成于专业
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20women%20fashion%20team%20in%20a%20bright%20modern%20studio%2C%20discussing%20clothing%20designs%20and%20fashion%20trends%2C%20warm%20natural%20lighting%2C%20cream%20interior%2C%20professional%20creative%20atmosphere%2C%20feminine%20aesthetic&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="团队风采"
                    className="w-full rounded-lg"
                    title="穿搭换装指南团队"
                  />
                </div>
                <div>
                  <h3 className="font-heading text-lg md:text-xl text-foreground-900 mb-4">
                    让每一位女性都能优雅换装
                  </h3>
                  <div className="space-y-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      穿搭换装指南创立于2024年，是一个专注于女性穿搭换装技巧分享的优质内容平台。我们的初心很简单——帮助每一位爱美女性掌握科学的换装方法，让日常生活变得更加优雅有序。
                    </p>
                    <p>
                      从脱衣顺序到穿搭搭配，从衣物收纳到时尚趋势，我们以专业、实用的内容，陪伴每一位女性在穿搭之路上不断进步。
                    </p>
                    <p>
                      我们的编辑团队由资深时尚编辑、形象顾问和生活方式达人组成，每一篇文章都经过精心策划和撰写，确保内容既专业又易懂。
                    </p>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-lg bg-background-100">
                      <div className="font-heading text-xl md:text-2xl text-primary-500">200+</div>
                      <div className="text-xs text-foreground-500 mt-1">原创文章</div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-background-100">
                      <div className="font-heading text-xl md:text-2xl text-accent-500">50万+</div>
                      <div className="text-xs text-foreground-500 mt-1">月活跃读者</div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-background-100">
                      <div className="font-heading text-xl md:text-2xl text-primary-500">98%</div>
                      <div className="text-xs text-foreground-500 mt-1">好评率</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-900 text-center mb-8">
                我们的核心价值
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className="ri-shield-check-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">专业可靠</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    每篇文章都经过深入研究，确保内容专业准确、实用有效。
                  </p>
                </div>
                <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className="ri-heart-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">贴心陪伴</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    站在女性视角，用温暖贴心的方式分享穿搭知识，陪伴成长。
                  </p>
                </div>
                <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-4">
                    <i className="ri-lightbulb-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">持续创新</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    紧跟时尚潮流，持续输出新鲜有趣的穿搭灵感和换装方法。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-900 text-center mb-8">
                读者口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-sm text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.location}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-sm text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed">{t.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-lg mx-auto">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-900 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议，欢迎随时联系我们
              </p>

              {formStatus === 'success' ? (
                <div className="p-6 rounded-lg bg-accent-100 border border-accent-200 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-200 mb-3">
                    <i className="ri-check-line text-2xl text-accent-700"></i>
                  </div>
                  <h3 className="font-heading text-base text-accent-800 mb-1">提交成功！</h3>
                  <p className="text-sm text-accent-700">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-4 text-sm text-accent-600 underline hover:text-accent-800 whitespace-nowrap"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4 p-6 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（限500字）"
                    ></textarea>
                  </div>

                  <div className="honeypot-wrapper">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-md bg-primary-100 border border-primary-200">
                      <p className="text-sm text-primary-700">{formError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-900 text-center mb-8">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.slice(0, 4).map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <h4 className="font-heading text-sm md:text-base text-foreground-800 pr-4">
                        {faq.question}
                      </h4>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-secondary-100 group-open:bg-primary-100 transition-colors">
                        <i className="ri-add-line text-sm text-foreground-600 group-open:hidden"></i>
                        <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                      </div>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-900">
                加入我们的穿搭社区
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                与数十万爱美女性一起，探索优雅穿搭的无限可能
              </p>
              <div className="mt-6">
                <Link
                  to="/"
                  className="inline-block px-8 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  回到首页开始探索
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}